import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/max';
import { BRAZIL_COUNTRY_CODE, BRAZIL_DDI, BRAZIL_PHONE_DDDS, BRAZIL_PHONE_DDD_SET } from './constants';
import type {
  InternationalPhoneCountryConfig,
  InternationalPhoneInferenceResult,
  InternationalPhoneRule,
  InternationalPhoneRulesIndex,
  RankedInternationalPhoneCandidate,
} from './types';

interface BuiltInternationalPhoneRulesIndex extends InternationalPhoneRulesIndex {
  rulesByCountryCode: ReadonlyMap<string, InternationalPhoneRule>;
  rulesByDdi: ReadonlyMap<string, readonly InternationalPhoneRule[]>;
}

interface BrazilPhoneHeuristic {
  ddd: string | null;
  hasPlausibleProgress: boolean;
  hasValidDdd: boolean;
  isValidNationalNumber: boolean;
  nationalNumber: string;
}

interface RegionDisplayNames {
  of(code: string): string | undefined;
}

type IntlWithDisplayNames = typeof Intl & {
  DisplayNames?: new (
    locales?: string | readonly string[],
    options?: {
      type: 'region';
    }
  ) => RegionDisplayNames;
};

let inMemoryRulesIndex: BuiltInternationalPhoneRulesIndex | null = null;

function normalizeDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function stripSingleLeadingZero(value: string): string {
  return value.startsWith('0') ? value.slice(1) : value;
}

function hasExplicitInternationalPrefix(value: string): boolean {
  const trimmedValue = value.trim();
  return trimmedValue.startsWith('+') || trimmedValue.startsWith('00');
}

function getExplicitInternationalDigits(value: string): string | null {
  if (!hasExplicitInternationalPrefix(value)) {
    return null;
  }

  const digitsOnly = normalizeDigits(value);

  if (value.trim().startsWith('00')) {
    return digitsOnly.slice(2);
  }

  return digitsOnly;
}

function normalizePhoneInputForParsing(value: string): string {
  if (!value.trim().startsWith('00')) {
    return value;
  }

  const digitsOnly = normalizeDigits(value);

  if (!digitsOnly.startsWith('00')) {
    return value;
  }

  return `+${digitsOnly.slice(2)}`;
}

function buildUniqueValues<T>(values: readonly T[]): readonly T[] {
  return Object.freeze(Array.from(new Set(values)));
}

function getSharedPrefixLength(left: string, right: string): number {
  let index = 0;

  while (index < left.length && index < right.length && left[index] === right[index]) {
    index += 1;
  }

  return index;
}

function getSharedSuffixLength(left: string, right: string): number {
  let sharedLength = 0;

  while (
    sharedLength < left.length &&
    sharedLength < right.length &&
    left[left.length - 1 - sharedLength] === right[right.length - 1 - sharedLength]
  ) {
    sharedLength += 1;
  }

  return sharedLength;
}

function getDigitsSimilarityScore(left: string, right: string): number {
  if (!left || !right) {
    return 0;
  }

  if (left === right) {
    return 30;
  }

  const prefixScore = getSharedPrefixLength(left, right);
  const suffixScore = getSharedSuffixLength(left, right) * 2;
  const lengthPenalty = Math.abs(left.length - right.length) * 2;

  return Math.max(0, Math.min(24, prefixScore + suffixScore - lengthPenalty));
}

function normalizeFormattingSkeleton(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function getFormattingSimilarityScore(left: string, right: string): number {
  return getDigitsSimilarityScore(normalizeFormattingSkeleton(left), normalizeFormattingSkeleton(right));
}

function getRegionDisplayNames(): RegionDisplayNames | null {
  const DisplayNames = (Intl as IntlWithDisplayNames).DisplayNames;

  if (!DisplayNames) {
    return null;
  }

  return new DisplayNames(['pt-BR', 'pt', 'en'], { type: 'region' });
}

function getCountryLabel(countryCode: CountryCode, displayNames: RegionDisplayNames | null): string {
  return displayNames?.of(countryCode) ?? countryCode;
}

function getBrazilNationalNumberCandidates(rawDigits: string, explicitDigits: string | null): readonly string[] {
  return buildUniqueValues(
    [
      explicitDigits?.startsWith(BRAZIL_DDI) ? explicitDigits.slice(BRAZIL_DDI.length) : null,
      explicitDigits?.startsWith(BRAZIL_DDI)
        ? stripSingleLeadingZero(explicitDigits.slice(BRAZIL_DDI.length))
        : explicitDigits
          ? stripSingleLeadingZero(explicitDigits)
          : null,
      rawDigits.startsWith(BRAZIL_DDI) && rawDigits.length > 11 ? rawDigits.slice(BRAZIL_DDI.length) : null,
      rawDigits.startsWith(BRAZIL_DDI) && rawDigits.length > 11
        ? stripSingleLeadingZero(rawDigits.slice(BRAZIL_DDI.length))
        : null,
      stripSingleLeadingZero(rawDigits),
      rawDigits,
    ].filter((value): value is string => !!value)
  );
}

function looksLikeArgentinaMobileLocalNumber(rawDigits: string, explicitDigits: string | null): boolean {
  if (explicitDigits) {
    return explicitDigits.startsWith('54');
  }

  return /^0\d{2,4}15\d{6,8}$/.test(rawDigits);
}

function getBrazilPhoneHeuristic(rawDigits: string, explicitDigits: string | null): BrazilPhoneHeuristic | null {
  if (explicitDigits && !explicitDigits.startsWith(BRAZIL_DDI)) {
    return null;
  }

  let bestHeuristic: BrazilPhoneHeuristic | null = null;

  for (const candidateNationalNumber of getBrazilNationalNumberCandidates(rawDigits, explicitDigits)) {
    const nationalNumber = stripSingleLeadingZero(candidateNationalNumber);

    if (!nationalNumber || nationalNumber.length > 11) {
      continue;
    }

    const ddd = nationalNumber.length >= 2 ? nationalNumber.slice(0, 2) : null;
    const hasValidDdd = !!ddd && BRAZIL_PHONE_DDD_SET.has(ddd);
    const hasPlausibleProgress =
      nationalNumber.length === 1
        ? BRAZIL_PHONE_DDDS.some((supportedDdd) => supportedDdd.startsWith(nationalNumber))
        : nationalNumber.length >= 2
          ? hasValidDdd
          : false;
    const parsedBrazilNumber = parsePhoneNumberFromString(nationalNumber, BRAZIL_COUNTRY_CODE);
    const isValidNationalNumber = hasValidDdd && !!parsedBrazilNumber?.isValid() && parsedBrazilNumber.country === 'BR';
    const nextHeuristic: BrazilPhoneHeuristic = {
      ddd,
      hasPlausibleProgress,
      hasValidDdd,
      isValidNationalNumber,
      nationalNumber,
    };

    if (!bestHeuristic) {
      bestHeuristic = nextHeuristic;
      continue;
    }

    const bestScore =
      (bestHeuristic.isValidNationalNumber ? 100 : 0) +
      (bestHeuristic.hasValidDdd ? 10 : 0) +
      (bestHeuristic.hasPlausibleProgress ? 5 : 0) -
      Math.abs(bestHeuristic.nationalNumber.length - 11);
    const nextScore =
      (nextHeuristic.isValidNationalNumber ? 100 : 0) +
      (nextHeuristic.hasValidDdd ? 10 : 0) +
      (nextHeuristic.hasPlausibleProgress ? 5 : 0) -
      Math.abs(nextHeuristic.nationalNumber.length - 11);

    if (nextScore > bestScore) {
      bestHeuristic = nextHeuristic;
    }
  }

  return bestHeuristic;
}

function buildRule(countryCode: CountryCode, displayNames: RegionDisplayNames | null): InternationalPhoneRule {
  return Object.freeze({
    ddi: getCountryCallingCode(countryCode),
    pais: getCountryLabel(countryCode, displayNames),
    sigla: countryCode,
  });
}

function buildInternationalPhoneRulesIndex(): BuiltInternationalPhoneRulesIndex {
  const displayNames = getRegionDisplayNames();
  const rules = Object.freeze(getCountries().map((countryCode) => buildRule(countryCode, displayNames)));
  const rulesByCountryCode = new Map(rules.map((rule) => [rule.sigla, rule]));
  const groupedRulesByDdi = rules.reduce<Map<string, InternationalPhoneRule[]>>((index, rule) => {
    const existingRules = index.get(rule.ddi);

    if (existingRules) {
      existingRules.push(rule);
      return index;
    }

    index.set(rule.ddi, [rule]);
    return index;
  }, new Map());
  const rulesByDdi = new Map(
    Array.from(groupedRulesByDdi.entries(), ([ddi, groupedRules]) => [ddi, Object.freeze([...groupedRules])])
  );
  const ddiLengthsDescending = Object.freeze(
    Array.from(new Set(rules.map((rule) => rule.ddi.length))).sort((left, right) => right - left)
  );
  const countryConfig: InternationalPhoneCountryConfig = {
    countryCodes: Object.freeze(rules.map((rule) => rule.sigla)),
    labels: Object.freeze(
      rules.reduce<Record<string, string>>((labels, rule) => {
        labels[rule.sigla] = rule.pais;
        return labels;
      }, {})
    ),
  };

  return {
    countryConfig,
    ddiLengthsDescending,
    rules,
    rulesByCountryCode,
    rulesByDdi,
  };
}

function getOrBuildRulesIndex(): BuiltInternationalPhoneRulesIndex {
  if (inMemoryRulesIndex) {
    return inMemoryRulesIndex;
  }

  inMemoryRulesIndex = buildInternationalPhoneRulesIndex();
  return inMemoryRulesIndex;
}

export function getCachedInternationalPhoneCountryConfig(): InternationalPhoneCountryConfig | null {
  return inMemoryRulesIndex?.countryConfig ?? null;
}

export async function loadInternationalPhoneRulesIndex(): Promise<InternationalPhoneRulesIndex> {
  return getOrBuildRulesIndex();
}

export async function loadInternationalPhoneCountryConfig(): Promise<InternationalPhoneCountryConfig> {
  return getOrBuildRulesIndex().countryConfig;
}

function findMatchingDdi(rulesIndex: BuiltInternationalPhoneRulesIndex, explicitDigits: string | null): string | null {
  if (!explicitDigits) {
    return null;
  }

  for (const ddiLength of rulesIndex.ddiLengthsDescending) {
    const ddi = explicitDigits.slice(0, ddiLength);

    if (rulesIndex.rulesByDdi.has(ddi)) {
      return ddi;
    }
  }

  return null;
}

function buildCandidate(
  rulesIndex: BuiltInternationalPhoneRulesIndex,
  countryCode: CountryCode,
  e164: string,
  nationalNumber: string,
  score: number,
  reasons: readonly string[]
): RankedInternationalPhoneCandidate | null {
  const rule = rulesIndex.rulesByCountryCode.get(countryCode);

  if (!rule) {
    return null;
  }

  return {
    countryCode,
    countryName: rule.pais,
    ddi: rule.ddi,
    e164,
    nationalNumber,
    reasons: Object.freeze([...reasons]),
    rule,
    score,
  };
}

function rankExplicitCandidates(
  rulesIndex: BuiltInternationalPhoneRulesIndex,
  phoneNumber: string
): readonly RankedInternationalPhoneCandidate[] {
  const normalizedInput = normalizePhoneInputForParsing(phoneNumber);
  const parsedPhoneNumber = parsePhoneNumberFromString(normalizedInput);

  if (!parsedPhoneNumber) {
    return [];
  }

  const explicitDigits = getExplicitInternationalDigits(phoneNumber);
  const matchingDdi = findMatchingDdi(rulesIndex, explicitDigits);
  const fallbackCountries = matchingDdi
    ? getInternationalPhoneRulesByDdi(rulesIndex, matchingDdi).map((rule) => rule.sigla)
    : [];
  const possibleCountries = parsedPhoneNumber.country
    ? [parsedPhoneNumber.country]
    : parsedPhoneNumber.getPossibleCountries().length
      ? parsedPhoneNumber.getPossibleCountries()
      : fallbackCountries;
  const baseScore = parsedPhoneNumber.isValid() ? 120 : parsedPhoneNumber.isPossible() ? 70 : 0;

  return Object.freeze(
    buildUniqueValues(possibleCountries)
      .map((countryCode, index) =>
        buildCandidate(
          rulesIndex,
          countryCode as CountryCode,
          parsedPhoneNumber.number,
          parsedPhoneNumber.nationalNumber,
          baseScore - index,
          parsedPhoneNumber.isValid() ? ['ddi-explicito', 'parse-valido'] : ['ddi-explicito', 'parse-possivel']
        )
      )
      .filter((candidate): candidate is RankedInternationalPhoneCandidate => !!candidate)
  );
}

function rankLocalCandidates(
  rulesIndex: BuiltInternationalPhoneRulesIndex,
  phoneNumber: string
): readonly RankedInternationalPhoneCandidate[] {
  const normalizedInput = normalizePhoneInputForParsing(phoneNumber);
  const rawDigits = normalizeDigits(phoneNumber);
  const explicitDigits = getExplicitInternationalDigits(phoneNumber);
  const brazilHeuristic = getBrazilPhoneHeuristic(rawDigits, explicitDigits);
  const candidates: RankedInternationalPhoneCandidate[] = [];

  for (const countryCode of rulesIndex.countryConfig.countryCodes as readonly CountryCode[]) {
    const parsedPhoneNumber = parsePhoneNumberFromString(normalizedInput, countryCode);

    if (!parsedPhoneNumber) {
      continue;
    }

    const isValid = parsedPhoneNumber.isValid();
    const isPossible = parsedPhoneNumber.isPossible();

    if (!isValid && !isPossible) {
      continue;
    }

    if (isValid && parsedPhoneNumber.country && parsedPhoneNumber.country !== countryCode) {
      continue;
    }

    const candidateRule = rulesIndex.rulesByCountryCode.get(countryCode);

    if (!candidateRule) {
      continue;
    }

    let score = isValid ? 180 : 45;
    const reasons = [isValid ? 'parse-valido' : 'parse-possivel'];
    const phoneType = parsedPhoneNumber.getType();

    if (
      !explicitDigits &&
      rawDigits.startsWith(candidateRule.ddi) &&
      parsedPhoneNumber.number === `+${rawDigits}` &&
      parsedPhoneNumber.nationalNumber.length >= 9
    ) {
      score += 20;
      reasons.push('ddi-semprefixo');
    }

    if (parsedPhoneNumber.country === countryCode) {
      score += 25;
      reasons.push('pais-identificado');
    }

    if (phoneType === 'MOBILE') {
      score += 18;
      reasons.push('tipo-mobile');
    } else if (phoneType === 'FIXED_LINE_OR_MOBILE') {
      score += 28;
      reasons.push('tipo-misto');
    } else if (phoneType === 'FIXED_LINE') {
      score += 8;
      reasons.push('tipo-fixo');
    } else if (phoneType) {
      score -= 8;
      reasons.push('tipo-especial');
    }

    score += getDigitsSimilarityScore(rawDigits, parsedPhoneNumber.nationalNumber);
    score += getDigitsSimilarityScore(
      rawDigits,
      `${parsedPhoneNumber.countryCallingCode}${parsedPhoneNumber.nationalNumber}`
    );
    score += getFormattingSimilarityScore(phoneNumber, parsedPhoneNumber.formatNational());

    if (countryCode === BRAZIL_COUNTRY_CODE) {
      if (brazilHeuristic?.isValidNationalNumber) {
        score += 30;
        reasons.push('ddd-br-valido');
      } else if (brazilHeuristic?.hasValidDdd) {
        score += 12;
        reasons.push('ddd-br-parcial');
      } else if (rawDigits.length >= 2) {
        score -= 30;
        reasons.push('ddd-br-invalido');
      }
    }

    if (
      countryCode === 'AR' &&
      phoneType === 'MOBILE' &&
      parsedPhoneNumber.country === 'AR' &&
      looksLikeArgentinaMobileLocalNumber(rawDigits, explicitDigits)
    ) {
      score += 35;
      reasons.push('argentina-mobile');
    }

    const candidate = buildCandidate(
      rulesIndex,
      countryCode,
      parsedPhoneNumber.number,
      parsedPhoneNumber.nationalNumber,
      score,
      reasons
    );

    if (candidate) {
      candidates.push(candidate);
    }
  }

  return Object.freeze(
    [...candidates].sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      return left.countryName.localeCompare(right.countryName);
    })
  );
}

function getInternationalPhoneRuleByCountryCode(
  rulesIndex: InternationalPhoneRulesIndex,
  countryCode: string
): InternationalPhoneRule | undefined {
  return (rulesIndex as BuiltInternationalPhoneRulesIndex).rulesByCountryCode.get(countryCode.toUpperCase());
}

function getInternationalPhoneRulesByDdi(
  rulesIndex: InternationalPhoneRulesIndex,
  ddi: string
): readonly InternationalPhoneRule[] {
  return (rulesIndex as BuiltInternationalPhoneRulesIndex).rulesByDdi.get(ddi) ?? [];
}

export function matchInternationalPhoneForCountry(
  rulesIndex: InternationalPhoneRulesIndex,
  phoneNumber: string,
  countryCode: string
): RankedInternationalPhoneCandidate | null {
  const normalizedCountryCode = countryCode.toUpperCase() as CountryCode;

  if (!phoneNumber.trim()) {
    return null;
  }

  const rule = getInternationalPhoneRuleByCountryCode(rulesIndex, normalizedCountryCode);

  if (!rule) {
    return null;
  }

  const explicitDigits = getExplicitInternationalDigits(phoneNumber);

  if (explicitDigits && !explicitDigits.startsWith(rule.ddi)) {
    return null;
  }

  const parsedPhoneNumber = parsePhoneNumberFromString(
    normalizePhoneInputForParsing(phoneNumber),
    normalizedCountryCode
  );

  if (!parsedPhoneNumber?.isValid()) {
    return null;
  }

  if (parsedPhoneNumber.country && parsedPhoneNumber.country !== normalizedCountryCode) {
    return null;
  }

  return buildCandidate(
    rulesIndex as BuiltInternationalPhoneRulesIndex,
    normalizedCountryCode,
    parsedPhoneNumber.number,
    parsedPhoneNumber.nationalNumber,
    200,
    explicitDigits ? ['ddi-explicito', 'parse-valido', 'pais-identificado'] : ['parse-valido', 'pais-identificado']
  );
}

function rankInternationalPhoneCandidates(
  rulesIndex: InternationalPhoneRulesIndex,
  phoneNumber: string
): readonly RankedInternationalPhoneCandidate[] {
  if (!phoneNumber.trim()) {
    return [];
  }

  const builtRulesIndex = rulesIndex as BuiltInternationalPhoneRulesIndex;

  return hasExplicitInternationalPrefix(phoneNumber)
    ? rankExplicitCandidates(builtRulesIndex, phoneNumber)
    : rankLocalCandidates(builtRulesIndex, phoneNumber);
}

export function inferInternationalPhone(
  rulesIndex: InternationalPhoneRulesIndex,
  phoneNumber: string
): InternationalPhoneInferenceResult {
  const rankedCandidates = rankInternationalPhoneCandidates(rulesIndex, phoneNumber);

  return {
    candidates: rankedCandidates,
    hasExplicitInternationalPrefix: hasExplicitInternationalPrefix(phoneNumber),
    normalizedDigits: normalizeDigits(phoneNumber),
    topCandidate: rankedCandidates[0] ?? null,
  };
}

export async function validateInternationalPhone(phoneNumber: string): Promise<boolean> {
  if (!hasExplicitInternationalPrefix(phoneNumber)) {
    return false;
  }

  return isValidPhoneNumber(normalizePhoneInputForParsing(phoneNumber));
}
